import { IntlShape } from 'react-intl';
import { csvCell } from 'modules/Orders/utils';
import {
  OrderReadiness,
  ReadinessCheck,
  ReadinessCheckStatus,
  ReadinessMetrics,
  ReadinessOrderStatus,
  ReadinessProblem,
  ReadinessVerdict,
  ReportingModeValue,
} from './types';

export const ORDER_STATUSES: ReadinessOrderStatus[] = [
  'ACCEPTED',
  'PENDING',
  'NONE',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
];

// Statuses whose pair still needs a new order; a pending order awaits a
// decision and an accepted one already covers the date.
const ISSUABLE_STATUSES: ReadinessOrderStatus[] = [
  'NONE',
  'REJECTED',
  'EXPIRED',
  'CANCELLED',
];

const CHECK_STATUSES: ReadinessCheckStatus[] = ['PASS', 'WARN', 'FAIL'];
const VERDICTS: ReadinessVerdict[] = ['GO', 'RISK', 'NO_GO'];
const MODES: ReportingModeValue[] = ['OFF', 'WARNING', 'ENFORCING'];

type Raw = Record<string, unknown>;

const isObject = (value: unknown): value is Raw =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const toNumber = (value: unknown): number | null => {
  const number =
    typeof value === 'string' && value.trim() ? Number(value) : value;
  return typeof number === 'number' && Number.isFinite(number) ? number : null;
};

const toText = (value: unknown) =>
  typeof value === 'string' && value.trim() ? value : null;

const oneOf = <T extends string>(value: unknown, allowed: T[]) =>
  allowed.includes(value as T) ? (value as T) : null;

const normalizeMetrics = (raw: unknown): ReadinessMetrics => {
  const metrics = isObject(raw) ? raw : {};
  const count = (key: keyof ReadinessMetrics) => toNumber(metrics[key]) ?? 0;
  const pairs = count('pairs');
  const accepted = count('accepted');
  return {
    pairs,
    accepted,
    pending: count('pending'),
    rejected: count('rejected'),
    expired: count('expired'),
    cancelled: count('cancelled'),
    none: count('none'),
    coveragePercent:
      toNumber(metrics.coveragePercent) ??
      (pairs ? Math.round((accepted / pairs) * 1000) / 10 : 100),
    reportingPairs: count('reportingPairs'),
    reportingPairsWithoutOrder: count('reportingPairsWithoutOrder'),
    hoursWithoutOrder: count('hoursWithoutOrder'),
    pendingDeadlineAfterDate: count('pendingDeadlineAfterDate'),
    needsCorrectionEntries: count('needsCorrectionEntries'),
  };
};

const normalizeCheck = (raw: unknown): ReadinessCheck | null => {
  if (!isObject(raw) || !toText(raw.key)) {
    return null;
  }
  const value = toNumber(raw.value);
  const threshold = toNumber(raw.threshold);
  const status = oneOf(raw.status, CHECK_STATUSES);
  if (!status || value === null) {
    return null;
  }
  return {
    key: String(raw.key),
    status,
    value,
    threshold,
    warnThreshold: toNumber(raw.warnThreshold),
  };
};

const normalizeProblem = (raw: unknown): ReadinessProblem | null => {
  if (!isObject(raw)) {
    return null;
  }
  const consultantId = toNumber(raw.consultantId);
  const clientId = toNumber(raw.clientId);
  if (consultantId === null || clientId === null) {
    return null;
  }
  const username = toText(raw.consultantUsername) || '';
  const reportedHours = toNumber(raw.reportedHours) ?? 0;
  return {
    consultantId,
    consultantName: toText(raw.consultantName) || username,
    consultantUsername: username,
    clientId,
    clientName: toText(raw.clientName) || '',
    orderStatus: oneOf(raw.orderStatus, ORDER_STATUSES) || 'NONE',
    orderNumber: toText(raw.orderNumber),
    decisionDeadline: toText(raw.decisionDeadline),
    ownerName: toText(raw.ownerName),
    reportedHours,
    hoursWithoutOrder: toNumber(raw.hoursWithoutOrder) ?? 0,
    lastReportedDate: toText(raw.lastReportedDate),
    reportedRecently:
      typeof raw.reportedRecently === 'boolean'
        ? raw.reportedRecently
        : reportedHours > 0,
    needsCorrection: toNumber(raw.needsCorrection) ?? 0,
  };
};

const deriveVerdict = (checks: ReadinessCheck[]): ReadinessVerdict | null => {
  if (!checks.length) {
    return null;
  }
  if (checks.some(({ status }) => status === 'FAIL')) {
    return 'NO_GO';
  }
  return checks.some(({ status }) => status === 'WARN') ? 'RISK' : 'GO';
};

// Reads the readiness response defensively: missing or malformed parts fall
// back to empty values instead of breaking the page. Returns undefined when
// the body is not a readiness object at all.
export const normalizeReadiness = (
  raw: unknown,
): OrderReadiness | undefined => {
  if (!isObject(raw)) {
    return undefined;
  }
  const checks = (Array.isArray(raw.checks) ? raw.checks : [])
    .map(normalizeCheck)
    .filter((check): check is ReadinessCheck => Boolean(check));
  return {
    date: toText(raw.date) || '',
    mode: oneOf(raw.mode, MODES),
    effectiveDate: toText(raw.effectiveDate),
    includeInternal: raw.includeInternal === true,
    metrics: normalizeMetrics(raw.metrics),
    checks,
    verdict: oneOf(raw.verdict, VERDICTS) || deriveVerdict(checks),
    problems: (Array.isArray(raw.problems) ? raw.problems : [])
      .map(normalizeProblem)
      .filter((problem): problem is ReadinessProblem => Boolean(problem)),
  };
};

export const problemKey = ({ consultantId, clientId }: ReadinessProblem) =>
  `${consultantId}-${clientId}`;

export const isIssuable = ({ orderStatus }: ReadinessProblem) =>
  ISSUABLE_STATUSES.includes(orderStatus);

export interface ProblemFilters {
  status: ReadinessOrderStatus | '';
  clientId: string;
  owner: string;
  reportingOnly: boolean;
  query: string;
}

export const filterProblems = (
  problems: ReadinessProblem[],
  { status, clientId, owner, reportingOnly, query }: ProblemFilters,
) => {
  const text = query.trim().toLocaleLowerCase();
  return problems.filter(
    (problem) =>
      (!status || problem.orderStatus === status) &&
      (!clientId || String(problem.clientId) === clientId) &&
      (!owner || problem.ownerName === owner) &&
      (!reportingOnly || problem.reportedRecently) &&
      (!text ||
        [
          problem.consultantName,
          problem.consultantUsername,
          problem.clientName,
          problem.orderNumber,
          problem.ownerName,
        ].some((value) => value?.toLocaleLowerCase().includes(text))),
  );
};

export interface IssueGroup {
  clientId: number;
  clientName: string;
  consultantIds: number[];
}

// Bulk issue works per client, so the selection splits into one group per
// client in name order.
export const groupForIssue = (
  problems: ReadinessProblem[],
  selected: Set<string>,
): IssueGroup[] => {
  const groups = new Map<number, IssueGroup>();
  problems
    .filter((problem) => selected.has(problemKey(problem)))
    .forEach(({ clientId, clientName, consultantId }) => {
      const group = groups.get(clientId) || {
        clientId,
        clientName,
        consultantIds: [],
      };
      if (!group.consultantIds.includes(consultantId)) {
        group.consultantIds.push(consultantId);
      }
      groups.set(clientId, group);
    });
  return Array.from(groups.values()).sort((a, b) =>
    a.clientName.localeCompare(b.clientName),
  );
};

export const bulkIssueUrl = ({ clientId, consultantIds }: IssueGroup) =>
  `/assignments/bulk?clientId=${clientId}&consultantIds=${consultantIds.join(
    ',',
  )}`;

// Readiness pairs mix contracts: an ACCEPTED employment-contract order is
// "Issued".
const READINESS_STATUS_LABELS: Partial<Record<ReadinessOrderStatus, string>> = {
  NONE: 'ORDER_REPORTING.STATUS_NONE',
  ACCEPTED: 'ORDERS.STATUS.ACCEPTED_OR_ISSUED',
};

export const orderStatusLabel = (
  intl: IntlShape,
  status: ReadinessOrderStatus,
) =>
  intl.formatMessage({
    id: READINESS_STATUS_LABELS[status] || `ORDERS.STATUS.${status}`,
  });

export const readinessCsv = (problems: ReadinessProblem[], intl: IntlShape) => {
  const label = (id: string) => intl.formatMessage({ id });
  return [
    [
      label('ORDERS.CONSULTANT'),
      label('ORDERS.CONSULTANT_USERNAME'),
      label('ORDERS.CLIENT'),
      label('ORDERS.STATUS'),
      label('ORDERS.NUMBER'),
      label('ORDERS.DEADLINE'),
      label('ORDERS.OWNER'),
      label('ORDER_REPORTING.COLUMN_HOURS'),
      label('ORDER_REPORTING.COLUMN_WITHOUT_ORDER'),
      label('ORDER_REPORTING.COLUMN_CORRECTIONS'),
      label('ORDER_REPORTING.COLUMN_LAST_REPORTED'),
    ],
    ...problems.map((problem) => [
      problem.consultantName,
      problem.consultantUsername,
      problem.clientName,
      orderStatusLabel(intl, problem.orderStatus),
      problem.orderNumber,
      problem.decisionDeadline,
      problem.ownerName,
      problem.reportedHours,
      problem.hoursWithoutOrder,
      problem.needsCorrection,
      problem.lastReportedDate,
    ]),
  ]
    .map((row) => row.map(csvCell).join(','))
    .join('\r\n');
};
